// =============================
// File: src/components/user/UserDocsViewer.jsx (FIXED)
// =============================
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Search, 
  ChevronRight, 
  BookOpen,
  Home,
  Menu,
  X
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function UserDocsViewer({ role = 'customer' }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [allDocs, setAllDocs] = useState([]);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Fetch categories and docs
  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const [categoriesRes, docsRes] = await Promise.all([
          axios.get(`${API_URL}/api/docs/categories`, { params: { role } }),
          axios.get(`${API_URL}/api/docs`, { params: { role } })
        ]);

        setCategories(categoriesRes.data);
        setAllDocs(docsRes.data);
      } catch (error) {
        console.error('Error fetching docs data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [role]);

  // Fetch specific doc if slug is provided
  useEffect(() => {
    if (!slug) {
      setCurrentDoc(null);
      return;
    }

    async function fetchDoc() {
      try {
        const response = await axios.get(`${API_URL}/api/docs/${slug}`);
        setCurrentDoc(response.data);
      } catch (error) {
        console.error('Error fetching doc:', error);
        setCurrentDoc(null);
      }
    }

    fetchDoc();
  }, [slug]);

  function handleDocClick(docSlug) {
    // Determine route based on role
    const baseRoute = role === 'driver' ? '/driver-docs' : role === 'provider' ? '/provider-docs' : '/user-docs';
    navigate(`${baseRoute}/${docSlug}`);
    setIsSidebarOpen(false);
  }

  function handleBackToHome() {
    const baseRoute = role === 'driver' ? '/driver-docs' : role === 'provider' ? '/provider-docs' : '/user-docs';
    navigate(baseRoute);
    setCurrentDoc(null);
  }

  function handleBackToApp() {
    // Go back to main app
    window.location.href = '/#/';
  }

  // Filter docs by search
  const filteredDocs = allDocs.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.short_description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group docs by category
  const docsByCategory = categories.map(category => ({
    ...category,
    docs: filteredDocs.filter(doc => doc.category_id === category.id)
  })).filter(cat => cat.docs.length > 0);

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-brand-primary to-pink-600 shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBackToApp}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-white" />
                <h1 className="text-xl sm:text-2xl font-extrabold text-white font-montserrat">
                  Instrucciones
                </h1>
              </div>
            </div>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              {isSidebarOpen ? (
                <X className="w-6 h-6 text-white" />
              ) : (
                <Menu className="w-6 h-6 text-white" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside className={`
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          fixed lg:static inset-y-0 left-0 z-40
          w-80 bg-white border-r border-slate-200 shadow-lg lg:shadow-none
          transition-transform duration-300 ease-in-out
          flex flex-col
        `}>
          {/* Search */}
          <div className="p-4 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar instrucciones..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat text-sm"
              />
            </div>
          </div>

          {/* Categories & Docs */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="h-6 bg-slate-200 rounded mb-2"></div>
                    <div className="h-4 bg-slate-100 rounded"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                {docsByCategory.map((category) => {
                  const IconComponent = LucideIcons[category.icon] || BookOpen;
                  
                  return (
                    <div key={category.id}>
                      <div className="flex items-center gap-2 mb-3">
                        <IconComponent className="w-5 h-5 text-brand-primary" />
                        <h3 className="font-bold text-slate-900 font-montserrat">
                          {category.name}
                        </h3>
                      </div>

                      <div className="space-y-1 ml-7">
                        {category.docs.map((doc) => (
                          <button
                            key={doc.id}
                            onClick={() => handleDocClick(doc.slug)}
                            className={`
                              w-full text-left px-3 py-2 rounded-lg transition-all
                              ${currentDoc?.slug === doc.slug
                                ? 'bg-brand-primary text-white font-semibold'
                                : 'text-slate-700 hover:bg-slate-100'
                              }
                            `}
                          >
                            <p className="text-sm font-montserrat truncate">
                              {doc.title}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {docsByCategory.length === 0 && (
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500 font-montserrat">
                      {searchQuery ? 'No se encontraron instrucciones' : 'No hay instrucciones disponibles'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* Mobile Overlay */}
        {isSidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-30"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto">
          {currentDoc ? (
            <DocContent doc={currentDoc} onBack={handleBackToHome} />
          ) : (
            <DocsHome 
              categories={docsByCategory}
              onDocClick={handleDocClick}
            />
          )}
        </main>
      </div>
    </div>
  );
}

// Home view (when no doc selected)
function DocsHome({ categories, onDocClick }) {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h2 className="text-3xl font-extrabold text-slate-900 font-montserrat mb-2">
          Centro de Ayuda
        </h2>
        <p className="text-slate-600 font-montserrat">
          Aprende a usar todas las funcionalidades de Fast Max
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        {categories.map((category) => {
          const IconComponent = LucideIcons[category.icon] || BookOpen;
          
          return (
            <div
              key={category.id}
              className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-primary to-brand-gold flex items-center justify-center">
                  <IconComponent className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 font-montserrat">
                  {category.name}
                </h3>
              </div>

              {category.description && (
                <p className="text-sm text-slate-600 font-montserrat mb-4">
                  {category.description}
                </p>
              )}

              <div className="space-y-2">
                {category.docs.slice(0, 3).map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => onDocClick(doc.slug)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors group"
                  >
                    <span className="text-sm text-slate-700 font-montserrat truncate">
                      {doc.title}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-brand-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </button>
                ))}

                {category.docs.length > 3 && (
                  <p className="text-xs text-slate-500 font-montserrat px-3 py-1">
                    +{category.docs.length - 3} más
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Document content renderer
function DocContent({ doc, onBack }) {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-600 font-montserrat mb-6">
        <button
          onClick={onBack}
          className="hover:text-brand-primary transition-colors flex items-center gap-1"
        >
          <Home className="w-4 h-4" />
          <span>Inicio</span>
        </button>
        <ChevronRight className="w-4 h-4" />
        <span className="text-slate-900 font-semibold">{doc.category_name}</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-slate-900 font-semibold truncate">{doc.title}</span>
      </div>

      {/* Doc Header */}
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-montserrat mb-4">
          {doc.title}
        </h1>
        {doc.short_description && (
          <p className="text-lg text-slate-600 font-montserrat">
            {doc.short_description}
          </p>
        )}
      </div>

      {/* Content Blocks */}
      <div className="prose prose-slate max-w-none">
        {doc.content_blocks.map((block) => (
          <ContentBlock key={block.id} block={block} />
        ))}
      </div>

      {/* Back Button */}
      <div className="mt-12 pt-6 border-t border-slate-200">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold font-montserrat transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </button>
      </div>
    </div>
  );
}

// Content block renderer
function ContentBlock({ block }) {
  switch (block.block_type) {
    case 'heading':
      const HeadingTag = `h${block.heading_level || 2}`;
      return React.createElement(
        HeadingTag,
        { className: "font-extrabold text-slate-900 font-montserrat mt-8 mb-4" },
        block.content
      );

    case 'paragraph':
      return (
        <p className="text-slate-700 font-montserrat leading-relaxed mb-4">
          {block.content}
        </p>
      );

    case 'image':
      return (
        <figure className="my-8">
          <img
            src={block.image_url}
            alt={block.image_alt || 'Imagen de instrucción'}
            className="w-full rounded-xl border border-slate-200 shadow-sm"
          />
          {block.image_caption && (
            <figcaption className="text-sm text-slate-600 font-montserrat text-center mt-3">
              {block.image_caption}
            </figcaption>
          )}
        </figure>
      );

    case 'list':
      const items = JSON.parse(block.content || '[]');
      const ListTag = block.list_type === 'numbered' ? 'ol' : 'ul';
      return React.createElement(
        ListTag,
        { className: `${block.list_type === 'numbered' ? 'list-decimal' : 'list-disc'} list-inside space-y-2 mb-4 text-slate-700 font-montserrat` },
        items.map((item, index) => React.createElement('li', { key: index }, item))
      );

    case 'divider':
      return <hr className="my-8 border-slate-200" />;

    default:
      return null;
  }
}

export default UserDocsViewer;